function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';
import { decrypt, encrypt, generateHash, generateKeyPair, generateSecret, getKeyFromPem, getPrvKeyPem, getPubKeyPem, hybridDecrypt, hybridEncrypt, symmetricEncrypt } from 'helpers/crypto';
import { LocalError } from 'helpers/errorTypes';
import ApiService from 'services/ApiService';
import App from 'modules/App';
import Account from 'modules/Account';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import messages from './messages';
function* fetchOrganizationMemberships({ payload }) {
    try {
        const { organizationUID, countryCode } = payload.organization;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = `/api/Organization/${organizationUID}/OrganizationMemberships`;
        const organizationMemberships = yield call(ApiService.crossRegionalRequest, region, requestUrl);
        yield put(actions.fetchOrganizationMembershipsSuccess(organizationMemberships));
    } catch (err) {
        yield put(actions.fetchOrganizationMembershipsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* makeDeleteMembership(organizationMembership, adminOrganizationMembership) {
    const { organizationUID, identityUID } = organizationMembership;
    const { countryCode } = adminOrganizationMembership.organization;
    const region = yield call(ApiService.findRegionByCountry, countryCode);
    const requestUrl = `/api/Organization/${organizationUID}/OrganizationMemberships/${identityUID}`;
    yield call(ApiService.crossRegionalRequest, region, requestUrl, {
        method: 'DELETE'
    });
    return organizationMembership;
}
//----------------------------------------------------------------------------------------------------------------------
function* decryptGcoClinicMembershipExchangeToken(encryptedExchangeToken) {
    const passphrase = yield select(Account.selectors.passphrase);
    const encryptedPrivateKey = yield select(Account.selectors.encryptedPrivateKey);
    const prvKeyObj = getKeyFromPem(encryptedPrivateKey, passphrase);
    return hybridDecrypt(encryptedExchangeToken, prvKeyObj);
}
function* fetchGcoClinicMembership(organizationUID, region) {
    const requestUrl = `/api/Gco/ClinicMembership/${organizationUID}`;
    return yield call(ApiService.crossRegionalRequest, region, requestUrl);
}
function* sendGcoClinicExchangeToken(organizationUID, encryptedExchangeToken, region) {
    const requestUrl = `/api/Gco/Clinic/${organizationUID}/ExchangeToken`;
    return yield call(ApiService.crossRegionalRequest, region, requestUrl, {
        method: 'POST',
        body: {
            encryptedExchangeToken
        }
    });
}
function* generateGcoClinicExchangeToken(organizationMembership, region) {
    const { organizationUID, publicKey } = organizationMembership.organization;
    const clinicMembership = yield call(fetchGcoClinicMembership, organizationUID, region);
    if (!clinicMembership) return;
    const { clinic, encryptedExchangeToken } = clinicMembership;
    if (!clinic.encryptedExchangeToken) {
        const exchangeToken = yield call(decryptGcoClinicMembershipExchangeToken, encryptedExchangeToken);
        const organizationPubKeyObj = getKeyFromPem(publicKey);
        const clinicEncryptedExchangeToken = hybridEncrypt(exchangeToken, organizationPubKeyObj);
        yield call(sendGcoClinicExchangeToken, organizationUID, clinicEncryptedExchangeToken, region);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* getAdminPrivateKey(keys) {
    const accountPassphrase = yield select(Account.selectors.passphrase);
    const accountEncryptedPrivateKey = yield select(Account.selectors.encryptedPrivateKey);
    const accountPrvKeyObj = getKeyFromPem(accountEncryptedPrivateKey, accountPassphrase);
    if (!accountPrvKeyObj) {
        throw new LocalError({
            code: 'NoPrivateKey'
        });
    }
    const { encryptedPassphrase, encryptedPrivateKey } = keys;
    const passphrase = decrypt(encryptedPassphrase, accountPrvKeyObj);
    const prvKeyObj = getKeyFromPem(encryptedPrivateKey, passphrase);
    if (!prvKeyObj) {
        throw new LocalError({
            code: 'NoOrganizationPrivateKey'
        });
    }
    return prvKeyObj;
}
//----------------------------------------------------------------------------------------------------------------------
function* approveMembership({ payload }) {
    try {
        const { organizationMembership, adminOrganizationMembership } = payload;
        const { countryCode } = adminOrganizationMembership.organization;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        yield call(generateGcoClinicExchangeToken, adminOrganizationMembership, region);
        const organizationPassphrase = uuid();
        const adminPrvKeyObj = yield call(getAdminPrivateKey, adminOrganizationMembership.keys);
        const pubKeyObj = getKeyFromPem(organizationMembership.publicKey);
        const encryptedPassphrase = encrypt(organizationPassphrase, pubKeyObj);
        const encryptedPrivateKey = getPrvKeyPem(adminPrvKeyObj, organizationPassphrase);
        const { organizationUID, identityUID } = organizationMembership;
        const requestUrl = `/api/Organization/${organizationUID}/OrganizationMemberships/${identityUID}/Approve`;
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: {
                encryptedPassphrase,
                encryptedPrivateKey
            }
        });
        yield put(actions.approveMembershipSuccess(_object_spread_props(_object_spread({}, organizationMembership), {
            status: 'Active'
        })));
    } catch (err) {
        yield put(actions.approveMembershipError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* rejectMembership({ payload }) {
    try {
        const { organizationMembership, adminOrganizationMembership } = payload;
        yield call(makeDeleteMembership, organizationMembership, adminOrganizationMembership);
        yield put(actions.rejectMembershipSuccess(organizationMembership));
    } catch (err) {
        yield put(actions.rejectMembershipError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* deleteMembership({ payload }) {
    try {
        const { organizationMembership, adminOrganizationMembership } = payload;
        yield call(makeDeleteMembership, organizationMembership, adminOrganizationMembership);
        yield put(actions.deleteMembershipSuccess(organizationMembership));
    } catch (err) {
        yield put(actions.deleteMembershipError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* generateInvitationKeys(invitationCode, businessEmail) {
    const passphrase = yield call(generateSecret);
    const { prvKeyObj, pubKeyObj } = yield call(generateKeyPair);
    const [invitationEncryptedPrivateKey, invitationPublicKey] = yield all([
        call(getPrvKeyPem, prvKeyObj, passphrase),
        call(getPubKeyPem, pubKeyObj)
    ]);
    const invitationHash = yield call(generateHash, invitationCode, businessEmail);
    const invitationEncryptedPassphrase = yield call(symmetricEncrypt, passphrase, invitationHash);
    return {
        invitationPublicKey,
        invitationEncryptedPrivateKey,
        invitationEncryptedPassphrase
    };
}
function* generateMembershipKeys(invitationKeys, adminKeys) {
    const adminPrvKeyObj = yield call(getAdminPrivateKey, adminKeys);
    const organizationPassphrase = yield call(generateSecret);
    const organizationEncryptedPrivateKey = getPrvKeyPem(adminPrvKeyObj, organizationPassphrase);
    const invitationPubKeyObj = getKeyFromPem(invitationKeys.invitationPublicKey);
    const organizationEncryptedPassphrase = encrypt(organizationPassphrase, invitationPubKeyObj);
    return {
        organizationEncryptedPassphrase,
        organizationEncryptedPrivateKey
    };
}
function* createInvitation({ payload }) {
    try {
        const { values, adminOrganizationMembership } = payload;
        const { organizationUID, countryCode } = adminOrganizationMembership.organization;
        const invitationCode = uuid();
        const { firstName, lastName, businessEmail, scope, isAdmin } = values;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        yield call(generateGcoClinicExchangeToken, adminOrganizationMembership, region);
        const keys = yield call(generateInvitationKeys, invitationCode, businessEmail);
        const membershipKeys = yield call(generateMembershipKeys, keys, adminOrganizationMembership.keys);
        const requestUrl = '/api/Invitation';
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: {
                invitationCode,
                subject: 'Organization',
                organizationUID,
                countryCode,
                scope,
                keys,
                details: _object_spread_props(_object_spread({}, membershipKeys), {
                    isAdmin
                }),
                information: {
                    firstName,
                    lastName,
                    email: businessEmail
                }
            }
        });
        yield put(actions.fetchOrganizationMemberships(adminOrganizationMembership.organization));
        yield take([
            actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_SUCCESS,
            actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR
        ]);
        yield put(actions.createInvitationSuccess());
    } catch (err) {
        yield put(actions.createInvitationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* cancelInvitation({ payload }) {
    try {
        const { organizationMembership, adminOrganizationMembership } = payload;
        const { organizationUID, identityUID } = organizationMembership;
        const { countryCode } = adminOrganizationMembership.organization;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = '/api/Invitation/cancel';
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: {
                subject: 'Organization',
                details: {
                    organizationUID,
                    identityUID
                }
            }
        });
        yield put(actions.cancelInvitationSuccess(organizationMembership));
    } catch (err) {
        yield put(actions.cancelInvitationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* resendInvitation({ payload }) {
    try {
        const { organizationMembership, adminOrganizationMembership } = payload;
        const { organizationUID, identityUID } = organizationMembership;
        const { countryCode } = adminOrganizationMembership.organization;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = `/api/Organization/${organizationUID}/OrganizationMemberships/${identityUID}/ResendInvitation`;
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST'
        });
        yield put(actions.resendInvitationSuccess(organizationMembership));
    } catch (err) {
        yield put(actions.resendInvitationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* grantAdmin({ payload }) {
    try {
        const { organizationMembership } = payload;
        const { organizationUID, identityUID } = organizationMembership;
        const requestUrl = `/api/Organization/${organizationUID}/OrganizationMemberships/${identityUID}/GrantAdmin`;
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST'
        });
        yield put(actions.grantAdminSuccess(_object_spread_props(_object_spread({}, organizationMembership), {
            isAdmin: true
        })));
    } catch (err) {
        yield put(actions.grantAdminError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* revokeAdmin({ payload }) {
    try {
        const { organizationMembership } = payload;
        const { organizationUID, identityUID } = organizationMembership;
        const requestUrl = `/api/Organization/${organizationUID}/OrganizationMemberships/${identityUID}/RevokeAdmin`;
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST'
        });
        yield put(actions.revokeAdminSuccess(_object_spread_props(_object_spread({}, organizationMembership), {
            isAdmin: false
        })));
    } catch (err) {
        yield put(actions.revokeAdminError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* sagas() {
    yield takeLatest(actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS, fetchOrganizationMemberships);
    yield takeLatest(actionTypes.APPROVE_MEMBERSHIP, approveMembership);
    yield takeLatest(actionTypes.REJECT_MEMBERSHIP, rejectMembership);
    yield takeLatest(actionTypes.DELETE_MEMBERSHIP, deleteMembership);
    yield takeLatest(actionTypes.CREATE_INVITATION, createInvitation);
    yield takeLatest(actionTypes.CANCEL_INVITATION, cancelInvitation);
    yield takeLatest(actionTypes.RESEND_INVITATION, resendInvitation);
    yield takeLatest(actionTypes.GRANT_ADMIN, grantAdmin);
    yield takeLatest(actionTypes.REVOKE_ADMIN, revokeAdmin);
}
export default [
    sagas
];
